/**
 * AcceptInvitePage (iter203d) — public entry point reached via the
 * "Activate your account" email link. Reads ``?token=…&email=…`` from
 * the URL, prompts the customer to pick a password, calls
 * ``/customer-auth/accept-invite``, and redirects to /login on success.
 *
 * Zero navigation chrome — this page is often the very first time a
 * new customer touches the portal, so it should feel like a focused
 * activation screen rather than the full dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, CheckCircle, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();
  const email = (params.get("email") || "").trim().toLowerCase();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  // iter204 — when the user is ALREADY signed in as the invited email
  // (e.g. they authenticated via Microsoft/Google/Apple OAuth first),
  // the backend has already consumed the invite in `_create_session_
  // and_redirect`. Skip the password form entirely: show a success
  // block and redirect them to the app.
  const [alreadyActive, setAlreadyActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get("/auth/me");
        const my = (me.data?.email || "").toLowerCase();
        if (!cancelled && my && email && my === email) {
          // Already signed in as the invitee — their OAuth flow already
          // consumed the invite server-side; just confirm and redirect.
          setAlreadyActive(true);
          setTimeout(() => {
            if (!cancelled) navigate("/", { replace: true });
          }, 1800);
        }
      } catch { /* not signed in — password form path */ }
    })();
    return () => { cancelled = true; };
  }, [email, navigate]);

  const mismatch = useMemo(
    () => confirm.length > 0 && password !== confirm,
    [password, confirm]
  );
  const tooShort = useMemo(() => password.length > 0 && password.length < 8, [password]);
  const canSubmit = !!token && !!email && password.length >= 8
    && !mismatch && !submitting;

  useEffect(() => {
    if (!token || !email) {
      setErr("This invite link is incomplete. Please open the link from your invite email directly.");
    }
  }, [token, email]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/customer-auth/accept-invite", { email, token, password });
      setDone(true);
      toast.success("Account activated — you can sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Activation failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center px-4"
         data-testid="accept-invite-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-5">
        <div className="text-center space-y-1">
          <div className="inline-flex items-center justify-center w-11 h-11 bg-blue-50 rounded-full border border-blue-200 mb-1">
            <LockKey size={22} weight="bold" className="text-blue-700" />
          </div>
          <h1 className="text-lg font-black tracking-tight">Activate your account</h1>
          <p className="text-xs text-slate-500">
            Set a password for <b className="text-slate-800">{email || "your account"}</b> to finish signing up.
          </p>
        </div>

        {alreadyActive ? (
          <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-4 text-center"
               data-testid="accept-invite-already-active">
            <CheckCircle size={28} weight="fill" className="text-emerald-600 inline-block mb-2" />
            <div className="font-bold text-emerald-800 mb-1">Your account is already active.</div>
            <div className="text-xs text-emerald-800">You signed in with a social provider — taking you to the app…</div>
          </div>
        ) : done ? (
          <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-4 text-center"
               data-testid="accept-invite-success">
            <CheckCircle size={28} weight="fill" className="text-emerald-600 inline-block mb-2" />
            <div className="font-bold text-emerald-800 mb-1">You&apos;re all set.</div>
            <div className="text-xs text-emerald-800">Redirecting you to sign in…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="accept-invite-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                New password
              </label>
              <input type="password"
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     data-testid="accept-invite-password"
                     autoComplete="new-password"
                     minLength={8}
                     placeholder="At least 8 characters"
                     className="w-full h-10 px-3 text-sm rounded-sm border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none" />
              {tooShort && (
                <div className="text-[11px] text-amber-700 mt-1">Password must be at least 8 characters.</div>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                Confirm password
              </label>
              <input type="password"
                     value={confirm}
                     onChange={(e) => setConfirm(e.target.value)}
                     data-testid="accept-invite-confirm"
                     autoComplete="new-password"
                     className="w-full h-10 px-3 text-sm rounded-sm border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none" />
              {mismatch && (
                <div className="text-[11px] text-red-700 mt-1">Passwords don&apos;t match.</div>
              )}
            </div>

            {err && (
              <div className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
                   data-testid="accept-invite-error">
                <Warning size={13} weight="bold" className="mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            <button type="submit" disabled={!canSubmit}
                    data-testid="accept-invite-submit"
                    className="w-full h-10 rounded-sm text-xs font-bold uppercase tracking-wider
                               bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Activating…" : "Activate account"}
            </button>

            <div className="text-center text-[11px] text-slate-500 pt-1">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-700 hover:underline font-bold">Sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
